import { FormEvent, useContext, useEffect, useRef, useState } from "react";
import { BsArrowLeft, BsSave } from "react-icons/bs";
import { Button, Form } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { datamodelEntityListUrl } from "../../../../platform/constants/backend-constants";
import { IColumn } from "../../../../shared/components/normal-table/normal-table";
import Spinner from "../../../../shared/components/spinner/spinner";
import Tooltip from "../../../../shared/components/tooltip/tooltip";
import AuthContext from "../../../../shared/context/auth-context";
import useHttp from "../../../../shared/hooks/use-http";
import { getAllData, getData } from "../../../../shared/utils/api";
import { RenderFields } from "../../../../shared/utils/render-fields";
import { useBack } from "../../../../shared/utils/use-back";
import { createUUID, validateLoadingStatusNew } from "../../../../shared/utils/utils";
import { ITableRowData } from "../../../components/data-table/data-table";
import "./process-type-configuration.scss";
import ProcessTypeStage from "../process-type-stage/process-type-stage";
import Checkbox from "../../../components/checkbox/checkbox";
import { useHttpGet } from "@/shared/hooks/use-httpNonTable";

interface IProcessTypeConfigurationProps {
    processType: ITableRowData | undefined;
    domainTypeId: string | undefined;
    onProcessTypeUpdated: (processType: ITableRowData) => void;
    onSaveProcessType: () => void;
    isDomainTypeUpdated: boolean;
    setIsDomainTypeUpdated: (isDomainTypeUpdated: boolean) => void;
}

const fieldsGeneral: IColumn[] = [
  {
    key: "name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
    controlled: true,
    extraClass: "half transBack"
  },
  {
    key: "process_type_id",
    displayName: "Process type id",
    dataType: {
      type: "text",
    },
    controlled: true,
    extraClass: "half transBack"
  },
  {
    key: "event_capture_screen_name",
    displayName: "Event capture screen",
    dataType: {
      type: "reference",
      values: [],
    },
    extraClass: "half transBack",
    onFocusEmpty: true
  }
];

const fieldsConsolidation: IColumn[] = [
  {
    key: "consolidate_on_entity_name",
    displayName: "Entity",
    dataType: {
      type: "reference",
      values: [],
    },
    extraClass: "half transBack",
    onFocusEmpty: true
  }, {
    key: "consolidation_timeframe",
    displayName: "Timeframe (in hours)",
    dataType: {
      type: "text"
    },
    subType: "number",
    onlyWholeNumbers: true,
    controlled: true,
    extraClass: "half transBack",
    required: false
  },
  {
    key: "consolidate_display_field_name",
    displayName: "Display field",
    dataType: {
      type: "reference",
      values: [],
    },
    extraClass: "half transBack",
    onFocusEmpty: true
  }
];

const blankStage: ITableRowData = {
  id: "",
  domain_stage_id: "",
  mandatory: false,
  process_type_stage_actions: [],
  process_type_stage_user_groups: []
};

const ProcessTypeConfiguration = ({processType, domainTypeId, onProcessTypeUpdated, onSaveProcessType, isDomainTypeUpdated, setIsDomainTypeUpdated}: IProcessTypeConfigurationProps) => {
  const authCtx = useContext(AuthContext);
  const { handleBack } = useBack();
  const [stages, setStages] = useState<ITableRowData[]>([]);
  const [validated, setValidated] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    sendRequest: getEntities,
    status: getEntitiesStatus,
    data: getEntitiesData,
    error: getEntitiesError
  } = useHttp(getAllData, false, authCtx.token);

  const {
    sendRequest: getEntity,
    status: getEntityStatus,
    data: getEntityData,
    error: getEntityError
  } = useHttpGet(getData, false, authCtx.token);

  const {
    sendRequest: getCaptureScreens,
    status: getCaptureScreensStatus,
    data: getCaptureScreensData,
    error: getCaptureScreensError
  } = useHttp(getAllData, false, authCtx.token, undefined, "manager");

  const {
    sendRequest: getStages,
    status: getStagesStatus,
    data: getStagesData,
    error: getStagesError
  } = useHttp(getAllData, false, authCtx.token, undefined, "manager");
  
  useEffect(() => {
    if (domainTypeId) {
      getStages(`domain_type/${domainTypeId}/stages`);
    }
  }, [domainTypeId]);

  useEffect(() => {
    if (getStagesStatus === "completed" && !getStagesError && getStagesData) {
      setStages(getStagesData.rows);
    }
  }, [getStagesStatus, getStagesData, getStagesError]);

  useEffect(() => {
    if (isDomainTypeUpdated) {
      getStages(`domain_type/${domainTypeId}/stages`);
      setIsDomainTypeUpdated(false);
    }
  }, [isDomainTypeUpdated]);

  useEffect(() => {
    getEntities(`${datamodelEntityListUrl}?order_by=name asc`);
    getCaptureScreens("captures?datamodel_type=event");
  }, []);

  useEffect(() => {
    if (processType?.consolidate_on_entity_id) {
      getEntity(`datamodel/entity/${processType.consolidate_on_entity_id}`);
    }
  }, [processType]);

  if (!processType) {
    return <div></div>;
  }

  const handleOnProcessTypeDataChange = (newValue: string, key?: string, id?: string) => {
    if (!key) {
      return;
    }
    const newProcessType = {...processType};
    newProcessType[key] = newValue;

    if (id && key === "event_capture_screen_name") {
      newProcessType.event_capture_screen_id = id;  
    }
    if (id && key === "consolidate_on_entity_name") {
      newProcessType.consolidate_on_entity_id = id;
    }

    if (id && key === "consolidate_display_field_name") {
      newProcessType.consolidate_display_field_id = id;
    }

    onProcessTypeUpdated(newProcessType);
  };

  const handleOnStageDataChange = (stage: ITableRowData) => {
    const newProcessType = {...processType};
    newProcessType.process_type_stages = newProcessType.process_type_stages.map((s: ITableRowData) => s.id === stage.id ? stage : s);
    onProcessTypeUpdated(newProcessType);
  };

  const handleOnStageDelete = (stageId: string) => {
    const newStages = processType.process_type_stages.filter((stage: ITableRowData) => stage.id !== stageId);
    onProcessTypeUpdated({...processType, process_type_stages: newStages});
  };

  const getAvailableStages = (stageId: string) => {
    const usedStages = processType.process_type_stages.map((stage: ITableRowData) => stage.domain_stage_id);
    return stages.filter((stage: ITableRowData) => !usedStages.includes(stage.id) || stage.id === stageId);
  };

  const handleProcessTypeAddStage = () => {
    const newStage = {...blankStage};
    newStage.id = createUUID();
    onProcessTypeUpdated({...processType, process_type_stages: [...processType.process_type_stages, newStage]});
  };

  const handleProcessTypeSave = (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const form = event?.currentTarget || formRef.current;
    if (form?.checkValidity() !== true) {
      setValidated(true);
      return;
    }

    onSaveProcessType();
  };

  const handleOnActiveChange = () => {
    onProcessTypeUpdated({ ...processType, active: !processType.active });
  };

  // Derive fieldsGeneral and fieldsConsolidation with updated values
  const generalFields = fieldsGeneral.map(field =>
    field.key === "event_capture_screen_name" && getCaptureScreensStatus === "completed" && getCaptureScreensData && !getCaptureScreensError
      ? { ...field, dataType: { ...field.dataType, values: getCaptureScreensData.rows.map((row: ITableRowData) => ({ id: row.id, name: row.name })) } }
      : field
  );

  const consolidationFields = fieldsConsolidation.map(field => {
    if (field.key === "consolidate_on_entity_name" && getEntitiesStatus === "completed" && getEntitiesData && !getEntitiesError) {
      return { ...field, dataType: { ...field.dataType, values: getEntitiesData.rows.map((row: ITableRowData) => ({ id: row.id, name: row.name })) } };
    }
    if (field.key === "consolidate_display_field_name" && getEntityStatus === "completed" && getEntityData && !getEntityError) {
      return { ...field, dataType: { ...field.dataType, values: getEntityData.fields.map((row: ITableRowData) => ({ id: row.id, name: row.field_name })) } };
    }
    return field;
  });

  return <div className="processTypeConfiguration">
    {validateLoadingStatusNew([getEntitiesStatus, getCaptureScreensStatus]) && <Spinner />}
    <div onClick={() => handleBack()} className="processTypeConfiguration-navButton">
      <BsArrowLeft />
    </div>
    <Form ref={formRef} noValidate validated={validated} onSubmit={handleProcessTypeSave}>
      <h4><b>General</b></h4>
      <Checkbox 
        label="Process type is active"
        checked={processType?.active}
        onClick={handleOnActiveChange}
      />
      <RenderFields
        fields={generalFields}
        data={processType}
        handleOnChange={handleOnProcessTypeDataChange}
      />
      <h4>
        <b>Consolidation</b>
      </h4>
      <RenderFields
        fields={consolidationFields}
        data={processType}
        handleOnChange={handleOnProcessTypeDataChange}
      />

      <h4><b>Stages</b></h4>
      <div className="processTypeConfiguration-stagesWrapper">
        {processType?.process_type_stages
          ?.sort((a: ITableRowData, b: ITableRowData) => (a.domain_stage_position ?? 0) - (b.domain_stage_position ?? 0))
          .map((stage: ITableRowData) => (
            <div className="processTypeConfiguration-stageWrapper" key={stage.id}>
              <ProcessTypeStage
                stages={getAvailableStages(stage.domain_stage_id)}
                processTypeStage={stage}
                onStageDataChange={handleOnStageDataChange}
                onStageDelete={handleOnStageDelete}
              />
            </div>
        ))}
      </div>
      <div className="processTypeConfiguration-addStageButtonWrapper">
        <Tooltip content={"Add new stage"} direction="top" delay={350}>
          <Button
            name="add"
            disabled={processType?.process_type_stages?.length === stages.length}
            onClick={() => handleProcessTypeAddStage()}
            className="processTypeConfiguration-addStageButton"
            variant="primary"
          >
            <AiOutlinePlus/>
          </Button>
        </Tooltip>
        <Tooltip content={"Save all changes"} direction="top" delay={350}>
          <Button
            name="save"
            type="submit"
            className="processTypeConfiguration-saveProcessTypeButton"
            disabled={processType?.process_type_stages?.length === 0}
          >
            <BsSave/>
          </Button>
        </Tooltip>
      </div>
    </Form>
  </div>;
};

export default ProcessTypeConfiguration;